'use client';

import React, { useState, useEffect } from 'react';
import { Sparkles, Zap, TrendingUp, Copy, Check, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useChat } from '@/lib/hooks/useChat';
import toast, { Toaster } from 'react-hot-toast';
import {
  trackScriptGenerationStarted,
  trackScriptGenerationSuccess,
  trackScriptGenerationFailure,
  trackScriptCopied,
} from '@/lib/analytics';

interface TrendingSignal {
  id: string;
  keyword: string;
  topic: string;
  platform: 'reddit' | 'hn' | 'producthunt';
  score: number;
  velocity24h: number;
}

interface GeneratedScript {
  hook: string;
  body: string;
  cta: string;
  hashtags: string[];
  estimatedDuration: string;
}

const TRENDING_SIGNALS: TrendingSignal[] = [
  { id: 'sig-1', keyword: 'gemini-2-flash-demo', topic: 'Google Gemini 2.0 Flash multimodal real-time capabilities', platform: 'reddit', score: 88, velocity24h: 241 },
  { id: 'sig-2', keyword: 'ai-agents-production-2026', topic: 'AI agents moving from demos to production in enterprise', platform: 'hn', score: 85, velocity24h: 198 },
  { id: 'sig-3', keyword: 'cursor-vs-copilot-2026', topic: 'Cursor AI vs GitHub Copilot developer productivity comparison', platform: 'reddit', score: 82, velocity24h: 174 },
  { id: 'sig-4', keyword: 'sd35-dalle3-comparison', topic: 'Stable Diffusion 3.5 vs DALL-E 3 image quality comparison', platform: 'producthunt', score: 79, velocity24h: 156 },
  { id: 'sig-5', keyword: 'finetuning-vs-rag', topic: 'Fine-tuning vs RAG decision framework for production LLMs', platform: 'reddit', score: 71, velocity24h: 98 },
];

const HOOK_STYLES = [
  { id: 'controversy', label: 'Controversy Hook', description: 'Challenge a common belief' },
  { id: 'data', label: 'Data Hook', description: 'Lead with a surprising stat' },
  { id: 'story', label: 'Story Hook', description: 'Open with a relatable scenario' },
  { id: 'question', label: 'Question Hook', description: 'Pose a thought-provoking question' },
];

const CTA_TYPES = [
  { id: 'follow', label: 'Follow for more' },
  { id: 'comment', label: 'Comment your take' },
  { id: 'save', label: 'Save this' },
  { id: 'link', label: 'Link in bio' },
];

const platformConfig = {
  reddit: { label: 'Reddit', className: 'bg-orange-500/10 text-orange-400 border border-orange-500/20' },
  hn: { label: 'HN', className: 'bg-amber-500/10 text-amber-400 border border-amber-500/20' },
  producthunt: { label: 'PH', className: 'bg-red-500/10 text-red-400 border border-red-500/20' },
};

function parseScriptFromResponse(raw: string): GeneratedScript | null {
  try {
    const jsonMatch = raw.match(/```json\s*([\s\S]*?)```/);
    if (jsonMatch) {
      return JSON.parse(jsonMatch[1]);
    }
    const directJson = raw.match(/\{[\s\S]*\}/);
    if (directJson) {
      return JSON.parse(directJson[0]);
    }
    return null;
  } catch {
    return null;
  }
}

export default function ReelsScriptGenerator() {
  const [selectedSignal, setSelectedSignal] = useState<TrendingSignal | null>(null);
  const [hookStyle, setHookStyle] = useState('controversy');
  const [ctaType, setCtaType] = useState('follow');
  const [customContext, setCustomContext] = useState('');
  const [generatedScript, setGeneratedScript] = useState<GeneratedScript | null>(null);
  const [rawScript, setRawScript] = useState('');
  const [copied, setCopied] = useState(false);
  const [showSignalPicker, setShowSignalPicker] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', false);

  useEffect(() => {
    if (error) {
      toast.error(error.message);
      if (selectedSignal) {
        trackScriptGenerationFailure({
          signal_keyword: selectedSignal.keyword,
          error_message: error.message,
        });
      }
    }
  }, [error, selectedSignal]);

  useEffect(() => {
    if (response && !isLoading) {
      const parsed = parseScriptFromResponse(response);
      if (parsed) {
        setGeneratedScript(parsed);
        setRawScript('');
        // Track successful script generation
        if (selectedSignal) {
          trackScriptGenerationSuccess({
            signal_keyword: selectedSignal.keyword,
            signal_platform: selectedSignal.platform,
            hook_style: hookStyle,
            cta_type: ctaType,
            estimated_duration: parsed.estimatedDuration,
          });
        }
      } else {
        setRawScript(response);
        setGeneratedScript(null);
        // Treat unparseable response as a failure
        if (selectedSignal) {
          trackScriptGenerationFailure({
            signal_keyword: selectedSignal.keyword,
            error_message: 'Response could not be parsed as structured script',
          });
        }
      }
    }
  }, [response, isLoading, selectedSignal, hookStyle, ctaType]);

  const handleGenerate = () => {
    if (!selectedSignal) {
      toast.error('Select a trending signal first');
      return;
    }

    // Track generation attempt
    trackScriptGenerationStarted({
      signal_keyword: selectedSignal.keyword,
      signal_platform: selectedSignal.platform,
      hook_style: hookStyle,
      cta_type: ctaType,
    });

    const hookStyleLabel = HOOK_STYLES.find(h => h.id === hookStyle)?.label || hookStyle;
    const ctaLabel = CTA_TYPES.find(c => c.id === ctaType)?.label || ctaType;

    const systemPrompt = `You are an expert Instagram Reels scriptwriter specializing in AI/tech content. 
You write punchy, high-retention scripts optimized for the Instagram algorithm.
Scripts must be 45-60 seconds when spoken aloud (roughly 120-150 words for the body).
Always respond with valid JSON only — no markdown prose outside the JSON block.`;

    const userPrompt = `Generate a complete Instagram Reels script from this trending signal:

SIGNAL DATA:
- Topic: ${selectedSignal.topic}
- Keyword: ${selectedSignal.keyword}
- Platform: ${selectedSignal.platform.toUpperCase()}
- Trend Score: ${selectedSignal.score}/100
- 24h Velocity: +${selectedSignal.velocity24h} posts
${customContext ? `- Additional Context: ${customContext}` : ''}

REQUIREMENTS:
- Hook Style: ${hookStyleLabel} — grab attention in the first 3 seconds
- CTA Type: ${ctaLabel} — end with a strong, specific call-to-action
- Tone: Authoritative but conversational, fast-paced
- Target: AI/tech-savvy audience on Instagram
- Body: 3-4 punchy sentences with a clear insight or comparison
- Include 8-12 relevant hashtags mixing broad (#AI) and niche (#${selectedSignal.keyword})

Respond ONLY with this JSON structure:
\`\`\`json
{
  "hook": "The opening line (max 15 words, must stop the scroll)",
  "body": "The main content (120-150 words, 3-4 sentences, include the key insight)",
  "cta": "The call-to-action line (max 12 words)",
  "hashtags": ["hashtag1", "hashtag2", ...],
  "estimatedDuration": "~XX seconds"
}
\`\`\``;

    setGeneratedScript(null);
    setRawScript('');

    sendMessage([
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt },
    ], { max_completion_tokens: 800 });
  };

  const handleCopy = () => {
    if (!generatedScript) return;
    const text = `HOOK:\n${generatedScript.hook}\n\nBODY:\n${generatedScript.body}\n\nCTA:\n${generatedScript.cta}\n\nHASHTAGS:\n${generatedScript.hashtags.map(h => `#${h.replace(/^#/, '')}`).join(' ')}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Script copied to clipboard');
      // Track copy event
      if (selectedSignal) {
        trackScriptCopied({ signal_keyword: selectedSignal.keyword });
      }
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRegenerate = () => {
    setGeneratedScript(null);
    setRawScript('');
    handleGenerate();
  };

  return (
    <>
      <Toaster position="top-right" toastOptions={{ style: { background: 'var(--card)', color: 'var(--foreground)', border: '1px solid var(--border)' } }} />
      <div className="card-elevated space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Sparkles size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">AI Script Generator</h2>
              <p className="text-2xs text-muted-foreground">Generate Reels scripts from trending signals</p>
            </div>
          </div>
          {generatedScript && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleRegenerate}
                disabled={isLoading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all disabled:opacity-50"
              >
                <RefreshCw size={12} className={isLoading ? 'animate-spin' : ''} />
                Regenerate
              </button>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs bg-primary/10 text-primary hover:bg-primary/20 transition-all border border-primary/20"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? 'Copied' : 'Copy Script'}
              </button>
            </div>
          )}
        </div>

        {/* Signal Picker */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Trending Signal</label>
          <button
            onClick={() => setShowSignalPicker(!showSignalPicker)}
            className="w-full flex items-center justify-between p-3 rounded-lg bg-secondary border border-border hover:border-border/80 transition-all text-left"
          >
            {selectedSignal ? (
              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                <span className={`shrink-0 px-1.5 py-0.5 rounded text-2xs font-semibold ${platformConfig[selectedSignal.platform].className}`}>
                  {platformConfig[selectedSignal.platform].label}
                </span>
                <span className="text-sm text-foreground truncate">{selectedSignal.topic}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <TrendingUp size={10} className="text-positive" />
                  <span className="text-2xs text-positive font-mono">+{selectedSignal.velocity24h}</span>
                </div>
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">Select a trending signal…</span>
            )}
            {showSignalPicker ? <ChevronUp size={14} className="text-muted-foreground shrink-0" /> : <ChevronDown size={14} className="text-muted-foreground shrink-0" />}
          </button>

          {showSignalPicker && (
            <div className="rounded-lg border border-border bg-card shadow-lg overflow-hidden fade-in">
              {TRENDING_SIGNALS.map((signal) => (
                <button
                  key={signal.id}
                  onClick={() => { setSelectedSignal(signal); setShowSignalPicker(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors border-b border-border last:border-0 ${selectedSignal?.id === signal.id ? 'bg-primary/5' : ''}`}
                >
                  <span className={`shrink-0 px-1.5 py-0.5 rounded text-2xs font-semibold ${platformConfig[signal.platform].className}`}>
                    {platformConfig[signal.platform].label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground truncate">{signal.topic}</p>
                    <p className="text-2xs text-muted-foreground font-mono">{signal.keyword}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-2xs font-mono text-muted-foreground">Score: <span className="text-foreground">{signal.score}</span></span>
                    <div className="flex items-center gap-0.5">
                      <Zap size={9} className="text-warning" />
                      <span className="text-2xs text-warning font-mono">+{signal.velocity24h}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Hook Style + CTA */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Hook Style</label>
            <div className="grid grid-cols-2 gap-1.5">
              {HOOK_STYLES.map((h) => (
                <button
                  key={h.id}
                  onClick={() => setHookStyle(h.id)}
                  className={`px-2 py-1.5 rounded-md text-xs font-medium transition-all text-left ${hookStyle === h.id ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-secondary text-muted-foreground hover:text-foreground hover:bg-muted border border-transparent'}`}
                >
                  {h.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Call-to-Action</label>
            <div className="grid grid-cols-2 gap-1.5">
              {CTA_TYPES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCtaType(c.id)}
                  className={`px-2 py-1.5 rounded-md text-xs font-medium transition-all ${ctaType === c.id ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-secondary text-muted-foreground hover:text-foreground hover:bg-muted border border-transparent'}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Advanced Options */}
        <div>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showAdvanced ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            Advanced context
          </button>
          {showAdvanced && (
            <div className="mt-2 fade-in">
              <textarea
                value={customContext}
                onChange={(e) => setCustomContext(e.target.value)}
                placeholder="Add extra context, angle, or specific talking points…"
                rows={2}
                className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:border-primary/50 transition-colors"
              />
            </div>
          )}
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={isLoading || !selectedSignal}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <RefreshCw size={14} className="animate-spin" />
              Generating Script…
            </>
          ) : (
            <>
              <Sparkles size={14} />
              Generate Reels Script
            </>
          )}
        </button>

        {/* Generated Script Output */}
        {generatedScript && (
          <div className="space-y-3 pt-1 border-t border-border fade-in">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Generated Script</p>
              <span className="text-2xs font-mono text-muted-foreground bg-secondary px-2 py-0.5 rounded">{generatedScript.estimatedDuration}</span>
            </div>

            {/* Hook */}
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-2xs font-semibold uppercase tracking-wider text-primary mb-1">🎣 Hook</p>
              <p className="text-sm font-medium text-foreground leading-snug">{generatedScript.hook}</p>
            </div>

            {/* Body */}
            <div className="p-3 rounded-lg bg-secondary border border-border">
              <p className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">📝 Body</p>
              <p className="text-sm text-foreground leading-relaxed">{generatedScript.body}</p>
            </div>

            {/* CTA */}
            <div className="p-3 rounded-lg bg-positive/5 border border-positive/20">
              <p className="text-2xs font-semibold uppercase tracking-wider text-positive mb-1">📣 CTA</p>
              <p className="text-sm font-medium text-foreground">{generatedScript.cta}</p>
            </div>

            {/* Hashtags */}
            <div className="flex flex-wrap gap-1.5">
              {generatedScript.hashtags.map((tag, i) => (
                <span key={i} className="px-2 py-0.5 bg-muted rounded-full text-2xs text-muted-foreground font-mono">
                  #{tag.replace(/^#/, '')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Raw fallback */}
        {rawScript && !generatedScript && (
          <div className="p-3 rounded-lg bg-secondary border border-border fade-in">
            <p className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Generated Script</p>
            <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{rawScript}</p>
          </div>
        )}
      </div>
    </>
  );
}
